import type {
  AspectHeatmapDto,
  LocationBreakdownDto,
} from '@up2stay/up2promo-api-client';
import { Card, cn } from '@up2stay/up2promo-ui';

import { CHART } from '@/core/components/reputation/reputation-chart-config';
import { formatReputationScoreValue } from '@/core/components/reputation/reputation-score-formula';
import { REPUTATION_SECTION_TITLE_CLASS } from '@/core/components/reputation/reputation-surface';
import type { Dictionary } from '@/core/providers/dictionary-provider';

type Dict = Dictionary['reputation']['analytics'];

/** Aspect columns shown next to the KPIs (heatmap is already ranked by mentions). */
const ASPECT_COLUMNS = 4;
const GRID =
  'grid grid-cols-[1.6fr_.6fr_.8fr_.7fr_1.2fr_.8fr_repeat(4,64px)] gap-2.5';

function scoreDot(row: LocationBreakdownDto): string {
  if (row.totalReviews === 0) return 'var(--muted-foreground)';
  const score = row.score.score;
  if (score >= 70) return 'var(--ok)';
  if (score >= 50) return CHART.neutral;
  return 'var(--destructive)';
}

function SentimentBar({ row }: { row: LocationBreakdownDto }) {
  const { positive, neutral, negative } = row.sentimentSplit;
  const total = positive + neutral + negative;
  if (total === 0) return null;
  return (
    <span className="flex h-2 overflow-hidden rounded-full">
      {[
        [positive, CHART.positive],
        [neutral, CHART.neutral],
        [negative, CHART.negative],
      ].map(([count, color], i) =>
        Number(count) > 0 ? (
          <span
            key={i}
            style={{
              width: `${(Number(count) / total) * 100}%`,
              background: String(color),
            }}
          />
        ) : null,
      )}
    </span>
  );
}

function NetCell({
  net,
  total,
  maxAbs,
  onClick,
}: {
  net: number;
  total: number;
  maxAbs: number;
  onClick: () => void;
}) {
  if (total === 0) {
    return (
      <span
        className="h-8 rounded-lg border border-dashed border-input"
        aria-hidden
      />
    );
  }
  const strong = Math.abs(net) >= maxAbs * 0.6 && net !== 0;
  const tone =
    net === 0
      ? 'bg-muted text-muted-foreground'
      : net > 0
        ? strong
          ? 'bg-[color:var(--ok)] text-white'
          : 'bg-[color:var(--ok-bg)] text-[color:var(--ok-fg)]'
        : strong
          ? 'bg-destructive text-white'
          : 'bg-destructive/15 text-destructive';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-8 items-center justify-center rounded-lg text-xs font-semibold hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        tone,
      )}
    >
      {net > 0 ? `+${net}` : net < 0 ? `−${Math.abs(net)}` : '0'}
    </button>
  );
}

/** Location benchmark: KPIs plus aspect net-sentiment cells (desktop grid, mobile list). */
export function AnalyticsLocationsCard({
  rows,
  names,
  heatmap,
  labels,
  locale,
  onSelectLocation,
  onDrilldown,
}: {
  rows: LocationBreakdownDto[];
  names: Map<string, string>;
  heatmap: AspectHeatmapDto | undefined;
  labels: Dict;
  locale: string;
  onSelectLocation: (locationId: string) => void;
  onDrilldown: (locationId: string, aspectKey: string) => void;
}) {
  const t = labels.benchmark;
  const aspects = (heatmap?.aspects ?? []).slice(0, ASPECT_COLUMNS);
  const cellsByLocation = new Map(
    (heatmap?.locations ?? []).map((row) => [row.locationId, row.cells]),
  );
  const maxAbs = Math.max(
    1,
    ...(heatmap?.locations ?? []).flatMap((row) =>
      row.cells.map((cell) => Math.abs(cell.net)),
    ),
  );

  return (
    <Card className="min-w-0 p-5 sm:p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className={REPUTATION_SECTION_TITLE_CLASS}>{t.title}</h2>
          <p className="text-muted-foreground mt-0.5 text-[12.5px]">
            {t.subtitle}
          </p>
        </div>
        <div className="text-muted-foreground hidden items-center gap-3 text-[11.5px] md:flex">
          {t.legend}
          <span className="flex gap-0.5" aria-hidden>
            <span className="h-2.5 w-[18px] rounded-[3px] bg-destructive/70" />
            <span className="h-2.5 w-[18px] rounded-[3px] bg-muted" />
            <span className="h-2.5 w-[18px] rounded-[3px] bg-[color:var(--ok)]/45" />
            <span className="h-2.5 w-[18px] rounded-[3px] bg-[color:var(--ok)]" />
          </span>
        </div>
      </div>

      {/* Desktop grid */}
      <div className="hidden min-w-0 overflow-x-auto md:block">
        <div className="min-w-[860px]">
          <div
            className={cn(
              GRID,
              'text-muted-foreground items-end border-b py-2 text-[11.5px]',
            )}
          >
            <span>{t.location}</span>
            <span>{t.score}</span>
            <span>{t.reviews}</span>
            <span>{t.rating}</span>
            <span>{t.sentiment}</span>
            <span>{t.responseRate}</span>
            {aspects.map((aspect) => (
              <span
                key={aspect.key}
                className="truncate text-center"
                title={aspect.label}
              >
                {aspect.label}
              </span>
            ))}
          </div>
          {rows.map((row) => {
            const cells = cellsByLocation.get(row.locationId);
            const name = names.get(row.locationId) ?? row.locationId;
            const empty = row.totalReviews === 0;
            return (
              <div
                key={row.locationId}
                className={cn(
                  GRID,
                  'items-center border-b py-3.5 text-[13px] last:border-b-0',
                  empty && 'text-muted-foreground',
                )}
              >
                <button
                  type="button"
                  onClick={() => onSelectLocation(row.locationId)}
                  className={cn(
                    'flex min-w-0 items-center gap-2 text-left font-semibold hover:underline',
                    !empty && 'text-foreground',
                  )}
                >
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ background: scoreDot(row) }}
                  />
                  <span className="truncate">{name}</span>
                </button>
                <span>
                  {empty ? '–' : formatReputationScoreValue(row.score)}
                </span>
                <span className="font-mono">
                  {row.totalReviews.toLocaleString(locale)}
                </span>
                <span>{empty ? '–' : `★ ${row.averageRating.toFixed(1)}`}</span>
                {empty ? (
                  <span className="text-xs">{t.noReviews}</span>
                ) : (
                  <SentimentBar row={row} />
                )}
                <span
                  className={cn(
                    !empty &&
                      row.responseRateWithText < 0.5 &&
                      'font-semibold text-[color:var(--warn-fg)]',
                  )}
                >
                  {empty
                    ? '–'
                    : `${Math.round(row.responseRateWithText * 100)} %`}
                </span>
                {aspects.map((aspect) => {
                  const cell = cells?.find((c) => c.key === aspect.key);
                  return (
                    <NetCell
                      key={aspect.key}
                      net={cell?.net ?? 0}
                      total={cell?.total ?? 0}
                      maxAbs={maxAbs}
                      onClick={() => onDrilldown(row.locationId, aspect.key)}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile list */}
      <ul className="md:hidden">
        {rows.map((row) => {
          const empty = row.totalReviews === 0;
          return (
            <li key={row.locationId} className="border-b last:border-b-0">
              <button
                type="button"
                onClick={() => onSelectLocation(row.locationId)}
                className="flex min-h-14 w-full items-center gap-2.5 py-3 text-left"
              >
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: scoreDot(row) }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-semibold">
                    {names.get(row.locationId) ?? row.locationId}
                  </span>
                  <span className="text-muted-foreground block text-[11.5px]">
                    {empty
                      ? t.noReviews
                      : t.mobileSummary
                          .replace('{count}', String(row.totalReviews))
                          .replace('{rating}', row.averageRating.toFixed(1))
                          .replace(
                            '{rate}',
                            String(Math.round(row.responseRateWithText * 100)),
                          )}
                  </span>
                </span>
                <span
                  className={cn(
                    'text-[17px] font-bold',
                    empty && 'text-muted-foreground',
                  )}
                >
                  {empty ? '–' : formatReputationScoreValue(row.score)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
